import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from '@emotion/styled';
import {
  fontSize,
  fontWeight,
  spacing,
  borderRadius,
  mediaMax,
} from 'presentations/views/styles';
import {
  primary,
  black,
  white,
  gray02,
  gray03,
  gray04,
  gray06,
  blue02,
  secondary,
  gray01,
} from 'presentations/views/styles/color';
import { imagePath, transformCmsImageUrl } from 'presentations/assets';
import ResourceCategoryList from 'presentations/views/components/molecules/ResourceCategoryList';
import ResourceTagList from 'presentations/views/components/molecules/ResourceTagList';
import ResourceAbout from 'presentations/views/components/molecules/ResourceAbout';
import ResourceShareList from 'presentations/views/components/organisms/ResourceShareList';
import { ProductType } from 'domains/models/blog_post/productType';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import Image from 'presentations/views/components/atoms/Image';
import Avatar from 'presentations/views/components/atoms/Avatar';
import { ResourceItem } from 'presentations/api/schema/resources';
import { formatToDate } from 'infras/datetime';
import Head from 'next/head';
import { resourcePath, webPath } from 'presentations/routes/path';
import { useRouter } from 'next/router';
import ResourceEmbedContent from 'presentations/views/components/molecules/ResourceEmbedContent';
import {
  getPersistentItem,
  setPersistentItem,
} from 'presentations/views/helpers/storage';

export interface Props {
  resource: ResourceItem;
  productType: ProductType;
  isShowAd: boolean;
}

const defaultUserSlug = 'shouji';
const tocIsOpenCacheKey = 'magazineTocIsOpen';
const tocIsOpenCacheExpire = 60 * 60 * 24 * 365;

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const [isOpenToc, setIsOpenToc] = useState<boolean>(true);
  const router = useRouter();

  useEffect(() => {
    const cache = getPersistentItem(tocIsOpenCacheKey);
    const isOpen = cache === true || cache === null;
    setIsOpenToc(isOpen);
  }, [tocIsOpenCacheKey]);

  const shareUrl = webPath(
    resourcePath(props.resource.categories[0].slug, props.resource.slug),
    router.locale,
  );

  const onClickTC = useCallback(
    (e: SyntheticEvent<HTMLLIElement | HTMLSpanElement>) => {
      const index = e.currentTarget.dataset.id;
      if (!index) return;
      const targets = document.querySelectorAll(
        '#js-resource-body h2, #js-resource-body h3',
      );
      if (targets && targets.length > 0) {
        const target = targets[Number(index)];
        window.location.hash = `#tc${index}`;
        target.scrollIntoView();
      }
    },
    [],
  );

  const onClickOpenToc = useCallback(
    (e: SyntheticEvent<HTMLLIElement | HTMLSpanElement>) => {
      const isOpen = !isOpenToc;
      setIsOpenToc(isOpen);
      setPersistentItem(tocIsOpenCacheKey, isOpen, tocIsOpenCacheExpire);
    },
    [tocIsOpenCacheKey, tocIsOpenCacheExpire, isOpenToc],
  );

  const tableOfContents = useMemo(() => {
    if (!props.resource.tableOfContents) return [];
    const rows: {
      id: number;
      text: string;
      children: { id: number; text: string }[];
    }[] = [];
    let index = -1;
    let id = 0;
    for (const tc of props.resource.tableOfContents) {
      if (tc.tagName === 'H2') {
        rows.push({ id, text: tc.text, children: [] });
        index++;
      } else if (tc.tagName === 'H3') {
        if (index < 0) continue;
        rows[index].children.push({ id, text: tc.text });
      }
      id++;
    }
    return rows;
  }, [props.resource.tableOfContents]);

  const mvImage =
    transformCmsImageUrl(props.resource.thumbnailUrl, { width: 690 }) || '';

  return (
    <ContentWrapper>
      <Head>
        <link rel="preload" href={mvImage} as="image" />
      </Head>
      <ContentContainer>
        <HeaderImageContainer>
          {props.resource.thumbnailUrl && (
            <HeaderImage
              src={mvImage}
              alt={props.resource.title}
              width={690}
              height={448}
            />
          )}
          <ResourceCategoryListContainer>
            <ResourceCategoryList categories={props.resource.categories} />
          </ResourceCategoryListContainer>
        </HeaderImageContainer>
        <Title>{props.resource.title}</Title>
        <InfomationContainer>
          {props.resource.author.slug === defaultUserSlug ? (
            <AuthorContainer>
              <AuthorImage
                src={'logo/symbol.svg'}
                width={40}
                height={40}
                alt={translate('siteName')}
              />
              <AuthorName>{translate('siteName')}</AuthorName>
            </AuthorContainer>
          ) : (
            <AuthorContainer>
              {props.resource.author.imageUrl && (
                <AuthorAvatar
                  src={props.resource.author.imageUrl}
                  alt={props.resource.author.name}
                />
              )}
              <AuthorName>{props.resource.author.name}</AuthorName>
            </AuthorContainer>
          )}
          <CreatedAt>{formatToDate(props.resource.updatedAt)}</CreatedAt>
        </InfomationContainer>
        {tableOfContents.length > 0 && (
          <TableOfContentsContainer>
            <TableOfContentsHeader>
              <TableOfContentsTitle>
                {t('table_of_contents')}
              </TableOfContentsTitle>
              <IconButton onClick={onClickOpenToc}>
                <TocCloseIcon name={`chevron-${isOpenToc ? 'up' : 'down'}`} />
              </IconButton>
            </TableOfContentsHeader>
            <TableOfContentsList isOpen={isOpenToc}>
              {tableOfContents.map((item, index) => (
                <TableOfContentsListItem key={item.id}>
                  <TableOfContentsListItemText
                    data-id={item.id}
                    onClick={onClickTC}
                  >
                    {index + 1}. {item.text}
                  </TableOfContentsListItemText>
                  <TableOfContentsListItemChildren>
                    {item.children.map((child) => (
                      <TableOfContentsListItemChild
                        key={child.id}
                        data-id={child.id}
                        onClick={onClickTC}
                      >
                        - {child.text}
                      </TableOfContentsListItemChild>
                    ))}
                  </TableOfContentsListItemChildren>
                </TableOfContentsListItem>
              ))}
              <TableOfContentsListItem
                data-id={tableOfContents.length}
                onClick={onClickTC}
              >
                {tableOfContents.length + 1}. {t('Summary')}
              </TableOfContentsListItem>
            </TableOfContentsList>
          </TableOfContentsContainer>
        )}
        <div id="js-resource-body">
          <BodyContainer
            dangerouslySetInnerHTML={{ __html: props.resource.body }}
          />
          {props.isShowAd && (
            <>
              <ResourceEmbedContent productType={props.productType} />
              <ResourceAbout productType={props.productType} />
            </>
          )}
        </div>
        <TagsContainer>
          <ResourceTagList tags={props.resource.tags} />
        </TagsContainer>
      </ContentContainer>
      <SideShareIconContainer>
        <SideShareIconStickyWrapper>
          <SideShareIconSticky>
            <ResourceShareList
              shareTitle={props.resource.title}
              shareUrl={shareUrl}
            />
          </SideShareIconSticky>
        </SideShareIconStickyWrapper>
      </SideShareIconContainer>
    </ContentWrapper>
  );
}

const ContentWrapper = styled.div`
  position: relative;
`;

const ContentContainer = styled.div``;

const HeaderImageContainer = styled.div`
  /* width: 690px; */
  height: 448px;
  position: relative;

  ${mediaMax.tablet} {
    width: 100%;
    height: auto;
    min-height: 198px;
    margin: ${spacing(20)} 0 0 0;
  }
`;

const HeaderImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${borderRadius()};
`;

const ResourceCategoryListContainer = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;

  ${mediaMax.tablet} {
    top: 8px;
    left: 8px;
  }
`;

const Title = styled.h1`
  margin: ${spacing(32)} 0 0 0;

  font-size: ${fontSize(28)};
  font-weight: ${fontWeight('bold')};
  line-height: 1.4;
  letter-spacing: 0.05em;
  overflow-wrap: break-word;

  ${mediaMax.tablet} {
    margin: ${spacing(20)} 0 0 0;
    font-size: ${fontSize(20)};
  }
`;

const InfomationContainer = styled.div`
  display: flex;
  flex-flow: row;
  margin: ${spacing(40)} 0 0 0;
  padding: ${spacing(24)} 0;

  border-top: 1px solid ${gray04};
  border-bottom: 1px solid ${gray04};

  ${mediaMax.tablet} {
    margin: ${spacing(20)} 0 0 0;
    padding: ${spacing(12)} 0;
  }
`;

const AuthorContainer = styled.div`
  display: flex;
`;

const AuthorImage = styled(Image)`
  width: 40px;
  height: 40px;
  border: none;

  ${mediaMax.tablet} {
    width: 30px;
    height: 30px;
  }
`;
const AuthorAvatar = styled(Avatar)`
  width: 40px;
  height: 40px;

  ${mediaMax.tablet} {
    width: 30px;
    height: 30px;
  }
`;

const AuthorName = styled.span`
  display: flex;
  align-items: center;
  margin: 0 0 0 ${spacing(16)};

  font-size: ${fontSize(14)};
  line-height: 160%;
  color: ${gray02};

  ${mediaMax.tablet} {
    margin: 0 0 0 ${spacing(6)};
    font-size: ${fontSize(12)};
  }
`;

const CreatedAt = styled.span`
  display: flex;
  align-items: center;
  margin-left: auto;

  font-size: ${fontSize(14)};
  line-height: 160%;
  color: ${gray02};

  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
  }
`;

const TableOfContentsContainer = styled.div`
  margin: ${spacing(32)} 0 0 0;
  padding: ${spacing(20)} ${spacing(40)} ${spacing(20)} ${spacing(40)};

  background-color: ${gray06};
  border-radius: ${borderRadius()};

  ${mediaMax.tablet} {
    margin: ${spacing(20)} 0 0 0;
    padding: ${spacing(12)} ${spacing(20)} ${spacing(12)} ${spacing(20)};
  }
`;

const TableOfContentsTitle = styled.div`
  display: flex;
  align-items: center;
  font-weight: ${fontWeight('bold')};
  line-height: 160%;

  ${mediaMax.tablet} {
    font-size: ${fontSize(13)};
  }
`;

const TableOfContentsList = styled.ul<{ isOpen: boolean }>`
  display: ${(props) => (props.isOpen ? 'block' : 'none')};
  margin-top: ${spacing(12)};
  ${mediaMax.tablet} {
    margin-top: ${spacing(8)};
  }
`;

const TableOfContentsListItem = styled.li`
  margin-bottom: ${spacing(4)};
  cursor: pointer;
  :last-of-type {
    margin-bottom: 0;
  }

  ${mediaMax.tablet} {
    font-size: ${fontSize(13)};
  }
`;
const TableOfContentsListItemText = styled.span`
  display: block;
`;

const TableOfContentsListItemChildren = styled.ul`
  padding-left: ${spacing(16)};
`;
const TableOfContentsListItemChild = styled.li`
  margin-bottom: ${spacing(4)};
  cursor: pointer;
  :last-of-type {
    margin-bottom: 0;
  }

  ${mediaMax.tablet} {
    font-size: ${fontSize(13)};
  }
`;

const BodyContainer = styled.div`
  margin: ${spacing(48)} 0 ${spacing(40)};
  letter-spacing: 0.05em;
  font-size: ${fontSize(16)};
  line-height: 1.8;

  ${mediaMax.tablet} {
    margin: ${spacing(20)} 0 ${spacing(20)} 0;
  }

  h2 {
    margin: 3rem 0 ${spacing(32)} 0;
    font-size: ${fontSize(28)};
    line-height: 1.5;
    /* padding-bottom: ${spacing(24)}; */
    /* border-bottom: 1px solid ${primary}; */

    ${mediaMax.tablet} {
      margin: 3rem 0 2rem 0;
      font-size: ${fontSize(24)};
    }
  }

  h3 {
    margin: 2rem 0 ${spacing(16)} 0;
    font-size: ${fontSize(20)};
    line-height: 1.5;
    background: ${gray06};
    padding: ${spacing(16)} ${spacing(24)};
    border-radius: ${borderRadius(6)};

    ${mediaMax.tablet} {
      margin: 2rem 0 1rem 0;
      font-size: ${fontSize(20)};
    }
  }

  h4 {
    margin: 2rem 0 ${spacing(16)} 0;
    line-height: 1.5;
    border-left: 4px solid ${primary};
    padding-left: ${spacing(12)};
    ${mediaMax.tablet} {
      margin: 2rem 0 1rem 0;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: ${borderRadius()};
    /* object-fit: cover; */
    margin-bottom: 1.5rem;
    border: 1px solid ${gray06};
  }

  p {
    margin: 0 0 ${spacing(24)} 0;
    line-height: 180%;

    ${mediaMax.tablet} {
      margin: 0 0 ${spacing(20)} 0;
    }

    :last-of-type {
      margin: 0 0 ${spacing(32)} 0;

      ${mediaMax.tablet} {
        margin: 0 0 ${spacing(24)} 0;
      }
    }
  }

  & > a,
  .IconArrow {
    display: flex;
    align-items: center;
    margin: ${spacing(12)} 0;
    color: ${black};
    text-decoration: none;
    &:before {
      font-family: 'Material Icons';
      font-size: ${fontSize(24)};
      content: 'chevron_right';
      color: ${primary};
      vertical-align: middle;
    }
    &:hover {
      color: ${primary};
    }
  }

  & > * a {
    text-decoration: underline;
  }

  blockquote {
    padding: ${spacing(40)} ${spacing(56)} ${spacing(32)} ${spacing(64)};
    margin: ${spacing(40)} 0;
    background-color: ${gray06};
    background-image: url(${imagePath('pages/resource/blockquote.svg')});
    background-repeat: no-repeat;
    background-position: ${spacing(24)} ${spacing(24)};
    border-radius: ${borderRadius()};
    font-size: ${fontSize(14)};

    ${mediaMax.tablet} {
      padding: ${spacing(32)} ${spacing(40)};
      margin: ${spacing(20)} 0;
      background-position: ${spacing(16)} ${spacing(16)};
      background-size: 16px;
      line-height: 1.6;
    }
  }

  ol {
    margin-bottom: ${spacing(32)};
    padding-left: 2em;
    list-style-type: decimal;

    ${mediaMax.tablet} {
      margin-bottom: ${spacing(20)};
    }
  }

  ol > li {
    margin-bottom: ${spacing(16)};

    ${mediaMax.tablet} {
      margin-bottom: ${spacing(12)};
    }

    :last-of-type {
      margin-bottom: 0;
    }
  }

  ul {
    margin-bottom: ${spacing(32)};

    ${mediaMax.tablet} {
      margin-bottom: ${spacing(20)};
    }
  }

  ul > li {
    position: relative;
    margin-bottom: ${spacing(16)};
    padding-left: 2em;

    ${mediaMax.tablet} {
      margin-bottom: ${spacing(12)};
    }

    :last-of-type {
      margin-bottom: 0;
    }

    &:before {
      position: absolute;
      top: 0;
      left: 8px;

      font-family: 'Material Icons';
      font-size: ${fontSize(12)};
      content: 'remove';
      color: ${black};
      vertical-align: middle;
    }
  }
  .TableBox {
    padding: 0;
    border-radius: 0;
    ${mediaMax.tablet} {
      overflow: auto;
    }
  }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  th {
    padding: ${spacing(8)};
    border: 1px solid ${gray03};
    font-weight: ${fontWeight('bold')};
    vertical-align: center;
    min-width: 130px;
    background-color: ${gray06};
  }
  td {
    padding: ${spacing(8)};
    border: 1px solid ${gray03};
    min-width: 130px;
    background-color: ${white};
  }

  div {
    margin-bottom: ${spacing(40)};
    padding: ${spacing(40)};
    background-color: ${blue02};
    border-radius: ${borderRadius()};
    line-height: 180%;

    ${mediaMax.tablet} {
      margin-bottom: ${spacing(20)};
      padding: ${spacing(24)};
    }
  }

  .CpLeadText {
    font-size: ${fontSize(16)};
    font-weight: ${fontWeight('bold')};
    ${mediaMax.tablet} {
      font-size: ${fontSize(16)};
    }
  }
  .AccentText {
    color: ${secondary};
  }
  .PromotionBox {
    border-radius: ${borderRadius(16)};
    text-align: center;
  }
  .PromotionCode {
    font-size: ${fontSize(12)};
    margin-bottom: ${spacing(24)};
    color: ${gray02};
    span {
      display: block;
      margin-top: ${spacing(8)};
      font-size: ${fontSize(24)};
      font-weight: ${fontWeight('bold')};
      color: ${black};
    }
  }
  .Btn {
    padding: ${spacing(16)} ${spacing(32)};
    margin: 0 0 ${spacing(8)};
    background: ${primary};
    color: ${white};
    display: inline-block;
    font-size: ${fontSize(14)};
    font-weight: ${fontWeight('bold')};
    border-radius: 50px;
    text-decoration: none;
  }
  .MemberRegistration {
    display: block;
    a {
      justify-content: center;
    }
  }
  .jetpack-video-wrapper {
    padding: 0;
    iframe {
      width: 100%;
      ${mediaMax.smartphone} {
        height: 100%;
      }
    }
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-flow: row;
  margin: 0 0 ${spacing(64)} 0;
  padding: ${spacing(16)} 0;

  border-top: 1px solid ${gray04};
  border-bottom: 1px solid ${gray04};

  ${mediaMax.tablet} {
    margin: 0 0 ${spacing(28)} 0;
    padding: ${spacing(12)} 0;
  }
`;

const SideShareIconContainer = styled.div`
  position: absolute;
  top: 20px;
  left: -72px;
  height: 100%;

  ${mediaMax.tablet} {
    display: none;
  }
`;

const SideShareIconStickyWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const SideShareIconSticky = styled.div`
  position: sticky;
  top: 96px;
  left: 0;
  height: auto;
`;

const TableOfContentsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const TocCloseIcon = styled(Icon)`
  color: ${gray02};
`;
